import { useMemo } from 'react'

import offerPageURL, { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import { findCheapestTourV2Variation } from 'lib/tours/tourUtils'
import { isTourV2Offer, isTourV2OfferSummary } from 'lib/offer/offerTypes'

export interface WishlistDisplayFields {
  title: string
  imageIds: Array<string>
  subtitle?: string
  imageId?: string
  deeplinkUrl?: string
  locationName?: string
  latitude?: number
  longitude?: number
}

const EMPTY_IMAGE_IDS: Array<string> = []

function extractImageIds(
  images?: ReadonlyArray<{ id?: string }>,
): Array<string> {
  if (!images?.length) return EMPTY_IMAGE_IDS
  return images.reduce<Array<string>>((acc, image) => {
    if (image.id) acc.push(image.id)
    return acc
  }, [])
}

export function useHotelWishlistDisplayFields(
  offer: App.HotelOffer | App.HotelOfferSummary,
): WishlistDisplayFields {
  const imageIds = useMemo(() => extractImageIds(offer.images), [offer.images])

  return useMemo(() => ({
    title: offer.property.name,
    imageIds,
    subtitle: offer.location,
    imageId: offer.image.id,
    deeplinkUrl: offerPageURL(offer),
    locationName: offer.location,
    latitude: offer.property.latitude,
    longitude: offer.property.longitude,
  }), [offer, imageIds])
}

export function useBedbankWishlistDisplayFields(
  offer: App.BedbankOffer | App.BedbankOfferSummary,
): WishlistDisplayFields {
  const imageIds = useMemo(() => extractImageIds(offer.images), [offer.images])

  return useMemo(() => ({
    title: offer.property.name,
    imageIds,
    subtitle: offer.property.location,
    imageId: offer.image.id,
    deeplinkUrl: offerPageURL(offer),
    locationName: offer.property.location,
    latitude: offer.property.latitude,
    longitude: offer.property.longitude,
  }), [offer, imageIds])
}

export function useRentalWishlistDisplayFields(
  offer: App.HotelOffer | App.HotelOfferSummary,
): WishlistDisplayFields {
  const imageIds = useMemo(() => extractImageIds(offer.images), [offer.images])

  return useMemo(() => ({
    title: offer.property.name,
    imageIds,
    subtitle: offer.location,
    imageId: offer.image.id,
    deeplinkUrl: offerPageURL(offer),
    locationName: offer.location,
    latitude: offer.property.latitude,
    longitude: offer.property.longitude,
  }), [offer, imageIds])
}

export function useTourWishlistDisplayFields(
  offer: App.TourOffer | App.TourOfferSummary | App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary,
  selectedVariation?: App.Tours.TourV2OfferVariation,
): WishlistDisplayFields {
  const variation = selectedVariation ??
    ((isTourV2Offer(offer) || isTourV2OfferSummary(offer))
      ? findCheapestTourV2Variation(offer)
      : undefined)

  const imageIds = useMemo(
    () => variation?.images ? extractImageIds(variation.images) : EMPTY_IMAGE_IDS,
    [variation?.images],
  )

  const subtitle = (isTourV2Offer(offer) || isTourV2OfferSummary(offer))
    ? offer.operatedBy
    : undefined

  return useMemo(() => ({
    title: offer.name,
    imageIds,
    subtitle,
    imageId: imageIds[0],
    deeplinkUrl: offerPageURL(offer),
    locationName: variation?.startLocation,
  }), [offer, imageIds, subtitle, variation?.startLocation])
}

export function useCruiseWishlistDisplayFields(
  offer: App.CruiseOffer,
): WishlistDisplayFields {
  const imageIds = useMemo(() => extractImageIds(offer.images), [offer.images])

  return useMemo(() => ({
    title: offer.name,
    imageIds,
    subtitle: offer.ship.name,
    imageId: imageIds[0],
    deeplinkUrl: offerPageURL(offer),
    locationName: offer.departurePort,
  }), [offer, imageIds])
}

export function useExperienceWishlistDisplayFields(
  offer: App.ExperienceOffer,
): WishlistDisplayFields {
  const imageIds = useMemo(() => extractImageIds(offer.images), [offer.images])

  return useMemo(() => ({
    title: offer.name,
    imageIds,
    subtitle: offer.location.name,
    imageId: imageIds[0],
    deeplinkUrl: getExperienceOfferPageURL(offer),
    locationName: offer.location.name,
    latitude: offer.location.latitude,
    longitude: offer.location.longitude,
  }), [offer, imageIds])
}

export function useFlightWishlistDisplayFields(
  journeys: ReadonlyArray<{ originAirportCode: string; destinationAirportCode: string }>,
  fareType?: string,
): WishlistDisplayFields {
  return useMemo(() => {
    const firstJourney = journeys[0]
    const title = firstJourney
      ? `${firstJourney.originAirportCode} to ${firstJourney.destinationAirportCode}`
      : 'Flight'

    return {
      title,
      imageIds: EMPTY_IMAGE_IDS,
      subtitle: fareType,
    }
  }, [journeys, fareType])
}
